/**
 * socketManager.ts
 *
 * A centralized Socket.IO manager to encapsulate connection logic, reconnection,
 * event bindings, and token refresh. Supports lifecycle hooks and pending emits.
 */

import { io, Socket } from "socket.io-client";
import type { ISocketManager } from "./ISocketManager";

// Define the socket URLs
const SOCKET_URL: string =
  import.meta.env.VITE_SOCKET_BASE_URL || "wss://api.daex.app";

type AsyncFunction = (...args: any[]) => Promise<any>;

export type socketEvents = {
  // Persistent listeners
  onUnauthenticated?: AsyncFunction;
  onConnected?: AsyncFunction;
  onDisconnected?: AsyncFunction;
  onUnauthorized?: AsyncFunction;
  // One-time listeners (for initial connection)
  onceConnectionError?: AsyncFunction;
  onceUnauthenticated?: AsyncFunction;
  onceDisconnected?: AsyncFunction;
  onceUnauthorized?: AsyncFunction;
};

export class SocketManager implements ISocketManager {
  /** Namespace for this socket (e.g., "tasks", "calendar") */
  public readonly namespace: string;

  /** Socket.IO instance */
  private socket: Socket | null = null;

  /** Event queue while not connected */
  private pendingEmits: { event: string; args: any[] }[] = [];

  /** Timeout duration for waitForConnection (ms) */
  private timeOut: number = 5000;

  /** Lifecycle and event handlers */
  private socketEvents: socketEvents = {};

  /** Flag to enable debug logging */
  private enableLogging: boolean;

  constructor(
    namespace: string,
    socketEvents: socketEvents,
    enableLogging?: boolean,
  ) {
    this.namespace = namespace;
    this.socketEvents = socketEvents;
    this.enableLogging = enableLogging || false;
    if (enableLogging) {
      console.debug(
        `[SocketManager] Initialized for namespace: "${this.namespace}"`,
      );
    }
  }

  /**
   * Registers all persistent `on` listeners on the socket
   */
  private handleEvents() {
    if (!this.socket) return;

    this.socket.on("connect", () => {
      this.enableLogging && console.debug(`[${this.namespace}] Connected`);
      // Emits made while disconnected (e.g. right after waking the phone)
      while (this.pendingEmits.length > 0) {
        const { event, args } = this.pendingEmits.shift()!;
        this.socket?.emit(event, ...args);
      }
      this.socketEvents.onConnected?.();
    });

    this.socket.on("unauthenticated", async () => {
      this.enableLogging && console.warn(`[${this.namespace}] Unauthenticated`);
      try {
        await this.socketEvents.onUnauthenticated?.();
      } catch (err) {
        console.error(`[${this.namespace}] onUnauthenticated failed:`, err);
        this.socket?.disconnect();
      }
    });

    this.socket.on("unauthorized", () => {
      this.enableLogging && console.warn(`[${this.namespace}] Unauthorized`);
      this.socketEvents.onUnauthorized?.();
      this.socket?.disconnect();
    });

    // No manual disconnect() here: it would stop Socket.IO from reconnecting.
    // iOS drops the connection in the background, the next emit after waking
    // then detects it - without reconnecting, all emits stayed queued until
    // the next token refresh (swipes did nothing, task pages kept loading)
    this.socket.on("disconnect", (reason) => {
      this.enableLogging &&
        console.warn(`[${this.namespace}] Disconnected`, reason);
      this.socketEvents.onDisconnected?.();
    });
  }

  /**
   * Waits for connection success or failure with timeout and once-listeners
   */
  private async waitForConnection(): Promise<void> {
    return new Promise((resolve, reject) => {
      const socket = this.socket;
      if (!socket) return reject(new Error("Socket not initialized"));

      // Only meant for the initial handshake: without removing them, a later
      // 'unauthorized' would still trigger onceUnauthorized (redirect to "/")
      const cleanup = () => {
        clearTimeout(timeout);
        socket.off("connect", onConnect);
        socket.off("connect_error", onConnectError);
        socket.off("unauthorized", onUnauthorized);
        socket.off("unauthenticated", onUnauthenticated);
        socket.off("disconnect", onDisconnect);
      };

      const timeout = setTimeout(() => {
        cleanup();
        reject(new Error("Socket connection timeout"));
      }, this.timeOut);

      const onConnect = () => {
        cleanup();
        resolve();
      };

      const onConnectError = (err: Error) => {
        cleanup();
        this.enableLogging &&
          console.error(`[${this.namespace}] Connection error`, err);
        this.socketEvents.onceConnectionError?.(err);
        reject(err);
      };

      const onUnauthorized = (err: any) => {
        cleanup();
        this.enableLogging &&
          console.error(`[${this.namespace}] Unauthorized (once)`, err);
        this.socketEvents.onceUnauthorized?.(err);
        reject(err);
      };

      const onUnauthenticated = () => {
        cleanup();
        this.enableLogging &&
          console.warn(`[${this.namespace}] Unauthenticated (once)`);
        this.socketEvents.onceUnauthenticated?.();
        reject(new Error("Socket unauthenticated"));
      };

      const onDisconnect = (reason: string) => {
        cleanup();
        this.enableLogging &&
          console.warn(
            `[${this.namespace}] Disconnected before connect`,
            reason,
          );
        this.socketEvents.onceDisconnected?.(reason);
        reject(new Error("Socket disconnected before connect"));
      };

      socket.once("connect", onConnect);
      socket.once("connect_error", onConnectError);
      socket.once("unauthorized", onUnauthorized);
      socket.once("unauthenticated", onUnauthenticated);
      socket.once("disconnect", onDisconnect);
    });
  }

  /**
   * Establishes the socket connection using current auth token
   */
  async connect() {
    const token = localStorage.getItem("daex-auth-token");
    if (!token) {
      console.warn("No auth token found, cannot connect socket.");
      throw Error();
    }
    this.socket = io(`${SOCKET_URL}/${this.namespace}`, {
      transports: ["websocket"],
      // Read on every (re)connect, so reconnects use the latest token
      auth: (cb) =>
        cb({ token: `Bearer ${localStorage.getItem("daex-auth-token")}` }),
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 10000,
    });
    this.handleEvents();
    await this.waitForConnection();
    return this.socket!;
  }

  /**
   * Called after token refresh to re-authenticate the socket
   */
  async handleTokenRefresh() {
    try {
      const newToken = localStorage.getItem("daex-auth-token");
      if (!newToken) return;

      if (!this.socket) {
        console.warn("Socket not initialized, cannot refresh token.");
        throw Error("Socket not initialized");
        return;
      }

      // Make sure we stop all event listeners and disconnect cleanly
      if (this.socket.connected) {
        // Wait for disconnect to complete
        await new Promise<void>((resolve) => {
          this.socket?.once("disconnect", () => {
            resolve();
          });
          this.socket?.disconnect();
        });
      }

      // Reconnect triggers a new handshake with the new token (auth reads it),
      // the connect handler replays pending emits
      this.socket.connect();
    } catch (e: any) {
      // Handle auth failure (redirect/logout)
      throw new Error(e.message);
    }
  }

  // Public Functions

  isConnected(): boolean {
    return this.socket?.connected ?? false;
  }

  /**
   * Reconnects when the socket is down and not already reconnecting
   * (called when the app wakes up)
   */
  ensureConnected() {
    if (this.socket && !this.socket.connected && !this.socket.active) {
      this.socket.connect();
    }
  }

  /**
   * Replaces the current set of lifecycle event handlers
   */
  setEvents(events: socketEvents) {
    this.socketEvents = events;
  }

  /**
   * Registers a one-time event listener (auto-removed after first trigger)
   */
  once<T = any>(event: string, callback: (payload: T) => void): void {
    if (!this.socket) {
      console.warn(
        `[SocketManager:${this.namespace}] Tried to register once("${event}") but socket not connected.`,
      );
      return;
    }
    this.socket.once(event, callback);
  }

  /**
   * Registers a persistent event handler
   */
  on<T = any>(event: string, callback: (payload: T) => void): void {
    if (!this.socket) {
      console.warn(
        `[SocketManager:${this.namespace}] Tried to register "${event}" but socket not connected.`,
      );
      return;
    }
    this.socket.on(event, callback);
  }

  /**
   * Removes one or all handlers for a given event
   */
  off(event: string, callback?: (...args: any[]) => void): void {
    if (!this.socket) return;
    if (callback) this.socket.off(event, callback);
    else this.socket.off(event); // remove all listeners for event
  }

  /**
   * Emits a socket event, or queues it if the socket is not yet connected
   */
  emit(event: string, ...args: any[]) {
    //showTokenLifetime();
    if (this.socket?.connected) {
      this.socket.emit(event, ...args);
    } else {
      this.pendingEmits.push({ event, args });
    }
  }

  /**
   * Disconnects the socket completely
   */
  disconnect() {
    this.socket?.disconnect();
    this.socket = null;
  }
}
